import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Empty,
  Flex,
  Icon,
  Input,
  Popconfirm,
  Space,
  Spin,
  Statistic,
  Tabs,
  Tag,
  Text,
  Title,
  cx,
  useToast,
} from "@app/ui";
import { listReviews, moderateReview } from "../api/dpp.js";
import type { ReviewItem } from "../api/dpp.js";
import { useCan } from "../auth/useCan.js";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";

const STATUS_COLOR: Record<ReviewItem["status"], "warning" | "success" | "default"> = {
  pending: "warning",
  approved: "success",
  rejected: "default",
};

const TABS = ["pending", "approved", "rejected", "all"] as const;
type TabKey = (typeof TABS)[number];

const TAB_LABEL_KEY = {
  pending: "reviews.statusPending",
  approved: "reviews.statusApproved",
  rejected: "reviews.statusRejected",
  all: "reviews.statusAll",
} as const;

function Stars({ rating }: { rating: number }) {
  return (
    <span className="rv-stars" role="img" aria-label={`${rating}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Icon key={i} name="star" size={14} fill={i <= rating ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

/** Review moderation queue — nothing shows publicly before an approve here. */
export function ReviewsPage() {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const fmtDate = useFormatDateTime();
  const can = useCan();
  const { org, loading: orgsLoading } = useOrgs();
  const canModerate = can("product:write");

  const [tab, setTab] = useState<TabKey>("pending");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  // One unfiltered fetch; tabs/search/KPIs all derive client-side so counts stay consistent.
  useEffect(() => {
    if (!org) return;
    setItems(null);
    setError(null);
    listReviews(org.id)
      .then(setItems)
      .catch((e) => setError(e));
  }, [org?.id]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    for (const r of items ?? []) c[r.status] += 1;
    return c;
  }, [items]);

  // Average of approved reviews only — the figure the public passport page shows.
  const avgApproved = useMemo(() => {
    const approved = (items ?? []).filter((r) => r.status === "approved");
    if (approved.length === 0) return null;
    return approved.reduce((sum, r) => sum + r.rating, 0) / approved.length;
  }, [items]);

  const moderate = async (reviewId: string, decision: "approved" | "rejected") => {
    if (!org) return;
    try {
      await moderateReview(org.id, reviewId, decision);
      setItems((prev) => prev && prev.map((r) => (r.id === reviewId ? { ...r, status: decision } : r)));
      toast.success(t(decision === "approved" ? "reviews.approvedToast" : "reviews.rejectedToast"));
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;
  if (error != null) return <ErrorAlert error={error} />;
  if (items === null) return <Spin />;

  const query = search.trim().toLowerCase();
  const matches = (r: ReviewItem) =>
    query === "" ||
    r.productName.toLowerCase().includes(query) ||
    (r.authorName ?? "").toLowerCase().includes(query) ||
    (r.body ?? "").toLowerCase().includes(query);

  const listFor = (key: TabKey) => items.filter((r) => (key === "all" || r.status === key) && matches(r));
  const countFor = (key: TabKey) => (key === "all" ? items.length : counts[key]);

  const actionsFor = (r: ReviewItem) => {
    if (!canModerate) return null;
    if (r.status === "pending") {
      return (
        <Space size="sm">
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="check" size={14} />}
            onClick={() => void moderate(r.id, "approved")}
          >
            {t("reviews.approve")}
          </Button>
          <Popconfirm
            title={t("reviews.rejectConfirm")}
            okText={t("reviews.reject")}
            cancelText={t("common.cancel")}
            okType="danger"
            onConfirm={() => void moderate(r.id, "rejected")}
          >
            <Button size="sm" variant="danger">
              {t("reviews.reject")}
            </Button>
          </Popconfirm>
        </Space>
      );
    }
    // Moderation is reversible: take an approved review down, or approve a rejected one.
    if (r.status === "approved") {
      return (
        <Popconfirm
          title={t("reviews.retractConfirm")}
          okText={t("reviews.retract")}
          cancelText={t("common.cancel")}
          okType="danger"
          onConfirm={() => void moderate(r.id, "rejected")}
        >
          <Button size="sm">{t("reviews.retract")}</Button>
        </Popconfirm>
      );
    }
    return (
      <Button size="sm" onClick={() => void moderate(r.id, "approved")}>
        {t("reviews.approve")}
      </Button>
    );
  };

  const reviewItem = (r: ReviewItem) => (
    <article key={r.id} className={cx("rv-item", r.status === "pending" && "rv-item--pending")}>
      <Flex justify="space-between" align="flex-start" gap="middle" wrap>
        <div className="rv-main">
          <Flex align="center" gap="small" wrap>
            <Stars rating={r.rating} />
            <Text strong>{r.authorName ?? t("reviews.anonymous")}</Text>
            <Text secondary small>
              ·
            </Text>
            <Link to={`/products/${r.productId}`}>{r.productName}</Link>
            <Text secondary small>
              ·
            </Text>
            <Text secondary small>
              {fmtDate(r.createdAt)}
            </Text>
          </Flex>
          <div className="rv-body">
            {r.body ? (
              <Text style={{ whiteSpace: "pre-wrap" }}>{r.body}</Text>
            ) : (
              <Text secondary small>
                {t("reviews.noBody")}
              </Text>
            )}
          </div>
        </div>
        <Flex align="center" gap="small">
          <Tag color={STATUS_COLOR[r.status]}>{t(`reviews.status_${r.status}`)}</Tag>
          {actionsFor(r)}
        </Flex>
      </Flex>
    </article>
  );

  const paneFor = (key: TabKey) => {
    const list = listFor(key);
    if (list.length === 0) {
      const description =
        query !== ""
          ? t("reviews.emptyFiltered")
          : key === "pending"
            ? t("reviews.emptyPending")
            : t("reviews.empty");
      return <Empty description={description} />;
    }
    return <div className="rv-list">{list.map(reviewItem)}</div>;
  };

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <div>
        <Title level={2} style={{ marginBottom: "var(--az-sp-1)" }}>
          {t("reviews.title")}
        </Title>
        <Text secondary>{t("reviews.subtitle")}</Text>
      </div>

      <div className="rv-kpis">
        <Card size="small" variant="outlined">
          <Statistic
            title={t("reviews.statusPending")}
            value={counts.pending}
            prefix={<Icon name="bell" size={18} />}
          />
        </Card>
        <Card size="small" variant="outlined">
          <Statistic
            title={t("reviews.statusApproved")}
            value={counts.approved}
            prefix={<Icon name="success" size={18} />}
          />
        </Card>
        <Card size="small" variant="outlined">
          <Statistic
            title={t("reviews.statusRejected")}
            value={counts.rejected}
            prefix={<Icon name="close" size={18} />}
          />
        </Card>
        <Card size="small" variant="outlined">
          <Statistic
            title={t("reviews.avgRating")}
            value={avgApproved ?? "—"}
            precision={1}
            suffix={avgApproved != null ? "/ 5" : undefined}
            prefix={
              <span className="rv-stars">
                <Icon name="star" size={18} fill="currentColor" />
              </span>
            }
          />
          <Text secondary small style={{ display: "block", marginTop: "var(--az-sp-2)" }}>
            {t("reviews.avgRatingNote")}
          </Text>
        </Card>
      </div>

      <Card>
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as TabKey)}
          extra={
            <Input
              size="sm"
              prefix={<Icon name="search" size={14} />}
              placeholder={t("reviews.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
            />
          }
          items={TABS.map((key) => ({
            key,
            label: `${t(TAB_LABEL_KEY[key])} (${countFor(key)})`,
            children: paneFor(key),
          }))}
        />
      </Card>
    </Space>
  );
}
